/**
 * Croatian Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const hr: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Nadzorna ploča',
  'nav.funds': 'Fondovi',
  'nav.investments': 'Ulaganja',
  'nav.investees': 'Primatelji ulaganja',
  'nav.esg': 'ESG Pregled',
  'nav.compliance': 'Usklađenost',
  'nav.reports': 'Izvješća',
  'nav.settings': 'Postavke',
  'nav.users': 'Korisnici',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Pokazatelji',
  'nav.taxonomy': 'EU Taksonomija',
  'nav.filings': 'Podnesci',
  'nav.calendar': 'Kalendar',
  'nav.submit': 'Podnošenje podataka',
  'nav.submissions': 'Moji podnesci',
  'nav.benchmarks': 'Referentne vrijednosti',
  'nav.messages': 'Poruke',
  'nav.portfolio': 'Portfelj',
  'nav.performance': 'Uspješnost fondova',
  'nav.esgSummary': 'ESG Sažetak',
  'nav.pipeline': 'Kanal',
  'nav.dueDiligence': 'Dubinska analiza',
  'nav.impact': 'Pokazatelji utjecaja',
  'nav.sdg': 'Mapiranje COR-ova',
  'nav.audit': 'Revizijski trag',
  'nav.snapshots': 'Snimke podataka',
  'nav.knowledgeGraph': 'Graf znanja',
  'nav.geographic': 'Geografski',
  'nav.bi': 'BI Studio',
  'nav.ai': 'AI Asistent',
  'nav.tasks': 'Zadaci',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Regulatorni monitor',

  // Common Actions
  'action.create': 'Stvori',
  'action.edit': 'Uredi',
  'action.delete': 'Obriši',
  'action.save': 'Spremi',
  'action.cancel': 'Odustani',
  'action.submit': 'Podnesi',
  'action.approve': 'Odobri',
  'action.reject': 'Odbij',
  'action.download': 'Preuzmi',
  'action.export': 'Izvezi',
  'action.import': 'Uvezi',
  'action.filter': 'Filtriraj',
  'action.search': 'Pretraži',
  'action.refresh': 'Osvježi',
  'action.close': 'Zatvori',
  'action.back': 'Natrag',
  'action.next': 'Sljedeće',
  'action.previous': 'Prethodno',
  'action.viewAll': 'Prikaži sve',
  'action.viewDetails': 'Prikaži detalje',
  'action.generate': 'Generiraj',
  'action.signOut': 'Odjava',
  'action.addNew': 'Dodaj novo',
  'action.invite': 'Pozovi',

  // Statuses
  'status.draft': 'Nacrt',
  'status.submitted': 'Podneseno',
  'status.validated': 'Validirano',
  'status.approved': 'Odobreno',
  'status.rejected': 'Odbijeno',
  'status.pending': 'Na čekanju',
  'status.inProgress': 'U tijeku',
  'status.overdue': 'Zakašnjelo',
  'status.compliant': 'Usklađeno',
  'status.filed': 'Podneseno',
  'status.active': 'Aktivno',
  'status.inactive': 'Neaktivno',
  'status.closed': 'Zatvoreno',
  'status.fundraising': 'Prikupljanje sredstava',
  'status.investing': 'Ulaganje',
  'status.harvesting': 'Žetva',
  'status.validating': 'Validacija',
  'status.review': 'U pregledu',
  'status.monitoring': 'Praćenje',
  'status.exiting': 'Izlazak',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Dubinska analiza',
  'status.disbursed': 'Isplaćeno',
  'status.restructured': 'Restrukturirano',
  'status.matured': 'Dospjelo',
  'status.written_off': 'Otpisano',
  'status.exited': 'Izašao',
  'status.incomplete': 'Nepotpuno',
  'status.open': 'Otvoreno',
  'status.in_progress': 'U tijeku',
  'status.completed': 'Dovršeno',
  'status.cancelled': 'Otkazano',
  'status.accepted': 'Prihvaćeno',
  'status.failed': 'Neuspjelo',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Pregled portfelja',
  'dashboard.complianceStatus': 'Status usklađenosti',
  'dashboard.collectionProgress': 'Napredak prikupljanja',
  'dashboard.activityFeed': 'Aktivnost',
  'dashboard.quickActions': 'Brze radnje',
  'dashboard.investmentPipeline': 'Investicijski kanal',
  'dashboard.totalFunds': 'Ukupno fondova',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktivna ulaganja',
  'dashboard.totalInvestees': 'Primatelji ulaganja',
  'dashboard.recentActivity': 'Nedavna aktivnost',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Ukupno obvezano',
  'lp.capitalCalled': 'Pozvani kapital',
  'lp.distributed': 'Distribuirano',
  'lp.netPosition': 'Neto pozicija',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Uspješnost fondova',
  'lp.esgSummary': 'ESG Sažetak',
  'lp.reportLibrary': 'Knjižnica izvješća',
  'lp.taxonomyAligned': 'Usklađeno s taksonomijom',
  'lp.paiCoverage': 'PAI Pokrivenost',
  'lp.portfolioPerformance': 'Uspješnost LP portfelja',

  // Fund Labels
  'fund.name': 'Naziv fonda',
  'fund.code': 'Šifra fonda',
  'fund.vintage': 'Godina osnivanja',
  'fund.size': 'Veličina fonda',
  'fund.targetSize': 'Ciljna veličina',
  'fund.currency': 'Valuta',
  'fund.strategy': 'Strategija',
  'fund.status': 'Status',
  'fund.sfdrCategory': 'SFDR Kategorija',
  'fund.geographyFocus': 'Geografski fokus',
  'fund.sectorFocus': 'Sektorski fokus',

  // Form Labels
  'form.name': 'Naziv',
  'form.email': 'E-pošta',
  'form.country': 'Država',
  'form.amount': 'Iznos',
  'form.currency': 'Valuta',
  'form.date': 'Datum',
  'form.description': 'Opis',
  'form.notes': 'Bilješke',
  'form.type': 'Vrsta',
  'form.period': 'Razdoblje',
  'form.year': 'Godina',
  'form.required': 'Obavezno',
  'form.optional': 'Neobavezno',

  // Table Headers
  'table.fund': 'Fond',
  'table.commitment': 'Obveza',
  'table.called': 'Pozvano',
  'table.distributed': 'Distribuirano',
  'table.tvpi': 'TVPI',
  'table.status': 'Status',
  'table.regulation': 'Regulativa',
  'table.filingType': 'Vrsta podneska',
  'table.reportingPeriod': 'Razdoblje',
  'table.createdAt': 'Stvoreno',
  'table.actions': 'Radnje',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'EU Taksonomija',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Potpunost podataka',
  'compliance.nextDeadline': 'Sljedeći rok',

  // Reports
  'report.quarterly': 'Tromjesečno izvješće',
  'report.annual': 'Godišnje izvješće',
  'report.paiStatement': 'PAI Izjava',
  'report.taxonomyReport': 'Izvješće o taksonomiji',
  'report.generatedAt': 'Generirano',
  'report.version': 'Verzija',

  // General
  'general.loading': 'Učitavanje...',
  'general.noData': 'Nema dostupnih podataka',
  'general.error': 'Došlo je do pogreške',
  'general.retry': 'Pokušaj ponovno',
  'general.total': 'Ukupno',
  'general.page': 'Stranica',
  'general.of': 'od',
  'general.showing': 'Prikaz',
  'general.results': 'rezultata',
  'general.language': 'Jezik',
  'general.english': 'Engleski',
  'general.french': 'Francuski',

  // Tour
  'tour.takeTour': 'Pokrenite obilazak',
  'tour.skip': 'Preskoči',
  'tour.next': 'Sljedeće',
  'tour.prev': 'Prethodno',
  'tour.finish': 'Završi',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigacija',
  'tour.sidebarContent': 'Koristite bočnu traku za navigaciju između upravljanja portfeljem, prikupljanja ESG podataka, usklađenosti, izvješća i analitičkih alata.',
  'tour.kpisTitle': 'Pregled portfelja',
  'tour.kpisContent': 'Ove kartice prikazuju vaš portfelj na prvi pogled — ukupni fondovi, AUM, aktivna ulaganja i broj investiranih.',
  'tour.actionsTitle': 'Brze radnje',
  'tour.actionsContent': 'Stvorite fondove, dodajte ulaganja, pozovite ulagatelje i generirajte izvješća jednim klikom.',
  'tour.languageTitle': 'Jezik',
  'tour.languageContent': 'Prebacujte se između 24 službena jezika EU. Cijelo sučelje se prevodi trenutno.',
  'tour.aiTitle': 'AI Asistent',
  'tour.aiContent': 'Postavljajte pitanja o svom portfelju u bilo kojem trenutku. AI asistent ima puni kontekst o vašim fondovima i ulaganjima.',
  'action.createFund': 'Stvori fond',
  'action.editFund': 'Uredi fond',
  'action.addInvestment': 'Dodaj ulaganje',
  'action.addInvestee': 'Dodaj ulagatelja',
  'action.editInvestee': 'Uredi ulagatelja',
  'action.createTemplate': 'Stvori predložak',
  'action.createSubmission': 'Stvori predaju',
  'action.createFiling': 'Stvori prijavu',
  'action.generateReport': 'Generiraj izvješće',
  'action.inviteUser': 'Pozovi korisnika',
  'action.sendInvitation': 'Pošalji pozivnicu',
  'action.saveChanges': 'Spremi promjene',
  'form.fundName': 'Naziv fonda',
  'form.fundCode': 'Šifra fonda',
  'form.sfdrCategory': 'SFDR Kategorija',
  'form.baseCurrency': 'Osnovna valuta',
  'form.targetSize': 'Ciljna veličina',
  'form.vintageYear': 'Godina osnivanja',
  'form.geographyFocus': 'Geografski fokus',
  'form.sectorFocus': 'Sektorski fokus',
  'form.investmentCode': 'Šifra ulaganja',
  'form.instrumentType': 'Vrsta instrumenta',
  'form.maturityDate': 'Datum dospijeća',
  'form.sector': 'Sektor',
  'form.countryCode': 'Kod države',
  'form.entityType': 'Vrsta subjekta',
  'form.region': 'Regija',
  'form.staffCount': 'Broj zaposlenih',
  'form.borrowers': 'Zajmoprimci',
  'form.contactName': 'Ime kontakta',
  'form.contactEmail': 'Kontakt e-mail',
  'form.templateName': 'Naziv predloška',
  'form.templateType': 'Vrsta predloška',
  'form.indicators': 'Pokazatelji',
  'form.reportingPeriod': 'Izvještajno razdoblje',
  'form.reportingYear': 'Izvještajna godina',
  'form.regulation': 'Regulativa',
  'form.filingType': 'Vrsta prijave',
  'form.reportType': 'Vrsta izvješća',
  'form.emailAddress': 'E-mail adresa',
  'form.role': 'Uloga',
  'form.selectFund': 'Odaberite fond...',
  'form.selectInvestee': 'Odaberite ulagatelja...',
  'table.code': 'Šifra',
  'table.investee': 'Ulagatelj',
  'table.instrument': 'Instrument',
  'table.amount': 'Iznos',
  'table.currency': 'Valuta',
  'table.maturity': 'Dospijeće',
  'table.name': 'Naziv',
  'table.country': 'Država',
  'table.type': 'Vrsta',
  'table.period': 'Razdoblje',
  'table.completion': 'Dovršenost',
  'table.submitted': 'Predano',
  'table.version': 'Verzija',
  'table.indicators': 'Pokazatelji',
  'table.dueDate': 'Rok',
  'table.generated': 'Generirano',
  'table.email': 'E-mail',
  'table.role': 'Uloga',
  'error.createFailed': 'Stvaranje nije uspjelo. Pokušajte ponovo.',
  'error.updateFailed': 'Ažuriranje nije uspjelo. Pokušajte ponovo.',
  'error.loadFailed': 'Učitavanje podataka nije uspjelo.',
};
